import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getPublicProviderProfilePath } from "../config/publicProviderProfiles";
import HomeGate, { HOME_GATE_EMAIL_ID, HOME_GATE_ID } from "../components/landing/HomeGate";
import V7ProductPhone from "../components/landing/V7ProductPhone";
import { marketingLoginUrl } from "../constants/marketingUrls";
import {
  DISCLOSURE_BROCHURE,
  FORM_ADV_PDF,
  RELATIONSHIP_SUMMARY_CRS,
} from "../constants/regulatoryUrls";
import { analyticsService, ctaIntentFromCampaign } from "../services/analyticsService";
import "./HomePage.css";

const CAPABILITIES = [
  {
    title: "Your risk and growth objectives",
    copy: "Set the growth you want, your preferred risk level, and the investment horizon for your money.",
  },
  {
    title: "Plutus designs strategy",
    copy: "We create a comprehensive strategy to meet your needs, or you choose.",
  },
  {
    title: "Continuous execution",
    copy: "Plutus trades and rebalances in your brokerage account as the underlying models move. You keep custody of the assets and can disconnect at any time.",
  },
];

const PROVIDERS = [
  { name: "Citrini Research", providerId: 9 },
  { name: "HFI Research", providerId: 10 },
  { name: "Aurelion Research", providerId: 11 },
  { name: "Le Shrub", providerId: null },
  { name: "AP Research", providerId: 4 },
  { name: "Eliant Capital", providerId: 5 },
];

const PRODUCT_VIEWS = ["Objectives", "Model allocation", "Recent changes"];
const STORY_PANELS = ["Adviser and execution", "Whole portfolio system", "Who we serve"];

const HOME_PAGE_THEME_COLORS = {
  light: "#f7f4ee",
  dark: "#111d32",
} as const;

const SAFEGUARDS = [
  {
    title: "You own and control your assets.",
    copy: "Plutus is designed so that we do not hold or control your funds. We operate under limited trading authority granted by your brokerage.",
  },
  {
    title: "Limited trading authority only.",
    copy: "Your brokerage grants us permission to execute trades on your behalf. That authority does not allow us to withdraw cash or transfer assets to any third party. The only account debit we can initiate is the advisory fee you authorize.",
  },
  {
    title: "You can disconnect at any time.",
    copy: "You may revoke Plutus's trading authority at any time through your brokerage. Your portfolio remains as-is upon disconnection.",
  },
  {
    title: "SEC-registered investment adviser.",
    copy: "Namsoft Advisors LLC is registered with the SEC as an investment adviser and operates under a fiduciary duty to act in clients' best interests. SEC registration does not imply a certain level of skill or training.",
  },
];

type MarketingLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  campaign: string;
  label: string;
  section: string;
  /** Sign-up CTAs stay on the page: scroll to the hero gate and focus the email. The href remains the login URL as the no-script fallback. */
  toGate?: boolean;
};

const HOME_GATE_DESTINATION = "#home-gate";

// The email field on the email step, the code field on the code step. Focus is
// immediate; the scroll waits a tick so the mobile menu's overflow lock (lifted
// by its own state update) is gone before the page moves.
const jumpToHomeGate = () => {
  const input = document.getElementById(HOME_GATE_EMAIL_ID)
    ?? document.getElementById(HOME_GATE_ID)?.querySelector("input");
  if (!input) return;
  input.focus({ preventScroll: true });
  if (typeof input.scrollIntoView !== "function") return;
  window.setTimeout(() => input.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
};

type TrackedSectionLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  analyticsLabel: string;
  analyticsSection: string;
  href: string;
};

type ExternalDocumentLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  documentName: string;
  section: string;
};

const MarketingLink: React.FC<MarketingLinkProps> = ({
  campaign,
  label,
  section,
  toGate = false,
  onClick,
  children,
  ...props
}) => {
  const intent = ctaIntentFromCampaign(campaign);
  const destination = marketingLoginUrl(campaign, { intent, origin: "homepage" });

  return (
    <a
      {...props}
      href={destination}
      data-attr={`cta:${campaign}`}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (toGate) {
          event.preventDefault();
          analyticsService.trackCTAClick({ campaign, label, section, destination: HOME_GATE_DESTINATION, intent });
          jumpToHomeGate();
          return;
        }
        analyticsService.trackCTAClick({ campaign, label, section, destination, intent });
      }}
    >
      {children}
    </a>
  );
};

const TrackedSectionLink: React.FC<TrackedSectionLinkProps> = ({
  analyticsLabel,
  analyticsSection,
  children,
  href,
  onClick,
  ...props
}) => (
  <a
    {...props}
    href={href}
    onClick={(event) => {
      onClick?.(event);
      if (!event.defaultPrevented) {
        analyticsService.trackNavigationClick(analyticsLabel, analyticsSection, href);
      }
    }}
  >
    {children}
  </a>
);

const ExternalDocumentLink: React.FC<ExternalDocumentLinkProps> = ({
  children,
  documentName,
  href,
  onClick,
  section,
  ...props
}) => (
  <a
    {...props}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(event) => {
      onClick?.(event);
      if (!event.defaultPrevented && href) {
        analyticsService.trackDocumentOpen(documentName, section, href);
      }
    }}
  >
    {children}
  </a>
);

const HomePage: React.FC<{ analyticsEnabled?: boolean }> = ({ analyticsEnabled = true }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const storyRef = useRef<HTMLElement>(null);
  const storyStickyRef = useRef<HTMLDivElement>(null);
  const productStageRef = useRef<HTMLDivElement>(null);
  const capabilitiesRef = useRef<HTMLDivElement>(null);
  const proofRef = useRef<HTMLDivElement>(null);
  const lifeRef = useRef<HTMLElement>(null);
  const servicesRef = useRef<HTMLElement>(null);
  const storyPanelsTrackedRef = useRef(new Set<number>());
  const productViewsTrackedRef = useRef(new Set<number>());
  const providerViewsTrackedRef = useRef(new Set<number>());

  const [heroIntroComplete, setHeroIntroComplete] = useState(false);
  const [storyImageReady, setStoryImageReady] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [capabilityIndex, setCapabilityIndex] = useState(0);
  const [capabilitiesVisible, setCapabilitiesVisible] = useState(false);
  const [capabilitiesPaused, setCapabilitiesPaused] = useState(false);
  const [productView, setProductView] = useState(0);
  const [productVisible, setProductVisible] = useState(false);
  const [providerIndex, setProviderIndex] = useState(0);
  const [providerChanging, setProviderChanging] = useState(false);
  const [proofVisible, setProofVisible] = useState(false);
  const [proofPaused, setProofPaused] = useState(false);

  const closeMobileMenu = useCallback(() => {
    if (mobileMenuOpen) analyticsService.trackMenuToggle(false);
    setMobileMenuOpen(false);
  }, [mobileMenuOpen]);

  const toggleMobileMenu = useCallback(() => {
    const nextOpen = !mobileMenuOpen;
    analyticsService.trackMenuToggle(nextOpen);
    setMobileMenuOpen(nextOpen);
  }, [mobileMenuOpen]);

  useLayoutEffect(() => {
    const existingThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const themeColor = existingThemeColor || document.createElement("meta");
    const previousThemeColor = existingThemeColor?.content;
    const documentRoot = document.documentElement;
    const previousRootBackgroundColor = documentRoot.style.backgroundColor;
    const hadHomePageRootClass = documentRoot.classList.contains("homepage-v7-root");
    const previousBodyBackgroundColor = document.body.style.backgroundColor;

    if (!existingThemeColor) {
      themeColor.name = "theme-color";
      document.head.appendChild(themeColor);
    }

    themeColor.content = HOME_PAGE_THEME_COLORS.light;
    documentRoot.style.backgroundColor = HOME_PAGE_THEME_COLORS.light;
    documentRoot.classList.add("homepage-v7-root");
    document.body.style.backgroundColor = HOME_PAGE_THEME_COLORS.light;
    document.body.classList.add("homepage-v7-body");
    return () => {
      if (existingThemeColor && previousThemeColor !== undefined) themeColor.content = previousThemeColor;
      else themeColor.remove();
      documentRoot.style.backgroundColor = previousRootBackgroundColor;
      if (!hadHomePageRootClass) documentRoot.classList.remove("homepage-v7-root");
      document.body.style.backgroundColor = previousBodyBackgroundColor;
      document.body.classList.remove("homepage-v7-body", "menu-open");
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("menu-open", mobileMenuOpen);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMobileMenu();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeMobileMenu, mobileMenuOpen]);

  useEffect(() => {
    if (!analyticsEnabled) return;
    analyticsService.trackHomePageView();
  }, [analyticsEnabled]);

  useEffect(() => {
    if (!analyticsEnabled) return;
    const root = rootRef.current;
    if (!root) return;

    const sections = Array.from(root.querySelectorAll<HTMLElement>("[data-analytics-section]"));
    const trackedSections = new Set<HTMLElement>();
    const trackSection = (section: HTMLElement) => {
      if (trackedSections.has(section)) return;
      trackedSections.add(section);
      analyticsService.trackSectionView(
        section.dataset.analyticsSection || section.id || "unknown",
        section.dataset.analyticsLabel || section.id || "Unknown section",
        sections.indexOf(section) + 1,
      );
    };

    if (typeof IntersectionObserver === "undefined") {
      if (sections[0]) trackSection(sections[0]);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        trackSection(entry.target as HTMLElement);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.01, rootMargin: "0px 0px -35% 0px" });

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [analyticsEnabled]);

  useEffect(() => {
    const root = rootRef.current;
    const nav = navRef.current;
    const logo = logoRef.current;
    const story = storyRef.current;
    const storySticky = storyStickyRef.current;
    if (!root || !nav || !logo) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const documentRoot = document.documentElement;
    const themeSections = Array.from(root.querySelectorAll<HTMLElement>("[data-nav-theme]"));
    let lastY = window.scrollY;
    let currentPageColor = "";
    let frame = 0;

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const updatePageMotion = () => {
      const y = window.scrollY;
      if (y > lastY + 1 && y > 50) nav.classList.add("is-compact");
      else if (y < lastY - 1 || y <= 50) nav.classList.remove("is-compact");

      if (!reducedMotion.matches) logo.style.transform = `rotate(${y * 0.05}deg)`;

      const activeSection = themeSections.find((section) => {
        const rect = section.getBoundingClientRect();
        return rect.top <= 40 && rect.bottom > 40;
      });
      const theme = document.body.classList.contains("menu-open")
        ? "dark"
        : activeSection?.dataset.navTheme || "light";
      const pageColor = theme === "dark"
        ? HOME_PAGE_THEME_COLORS.dark
        : HOME_PAGE_THEME_COLORS.light;
      nav.dataset.theme = theme;
      if (currentPageColor !== pageColor) {
        if (themeColor) themeColor.content = pageColor;
        documentRoot.style.backgroundColor = pageColor;
        document.body.style.backgroundColor = pageColor;
        currentPageColor = pageColor;
      }

      if (story && storySticky && !reducedMotion.matches) {
        const rect = story.getBoundingClientRect();
        const distance = Math.max(1, story.offsetHeight - window.innerHeight);
        const progress = clamp(-rect.top / distance, 0, 1);
        const coastCopyExit = clamp((progress - 0.24) / 0.1, 0, 1);
        const mountainCopyReveal = clamp((progress - 0.26) / 0.1, 0, 1);
        const mountainCopyExit = clamp((progress - 0.57) / 0.1, 0, 1);
        const riverCopyReveal = clamp((progress - 0.61) / 0.1, 0, 1);
        const coast = 1 - clamp((progress - 0.2) / 0.18, 0, 1);
        const mountain = Math.min(
          clamp((progress - 0.2) / 0.18, 0, 1),
          1 - clamp((progress - 0.55) / 0.18, 0, 1),
        );
        const river = clamp((progress - 0.55) / 0.18, 0, 1);

        storySticky.style.setProperty("--story-scale", String(1.06 - progress * 0.06));
        storySticky.style.setProperty("--story-shade", String(0.22 + progress * 0.48));
        storySticky.style.setProperty("--story-frame-coast", String(coast));
        storySticky.style.setProperty("--story-frame-mountain", String(mountain));
        storySticky.style.setProperty("--story-frame-river", String(river));
        storySticky.style.setProperty("--story-copy-coast-opacity", String(1 - coastCopyExit));
        storySticky.style.setProperty("--story-copy-coast-blur", `${coastCopyExit * 10}px`);
        storySticky.style.setProperty("--story-copy-coast-y", `${-coastCopyExit * 24}px`);
        storySticky.style.setProperty("--story-copy-mountain-opacity", String(mountainCopyReveal * (1 - mountainCopyExit)));
        storySticky.style.setProperty("--story-copy-mountain-blur", `${(1 - mountainCopyReveal + mountainCopyExit) * 10}px`);
        storySticky.style.setProperty("--story-copy-mountain-y", `${(1 - mountainCopyReveal) * 32 - mountainCopyExit * 24}px`);
        storySticky.style.setProperty("--story-copy-river-opacity", String(riverCopyReveal));
        storySticky.style.setProperty("--story-copy-river-blur", `${(1 - riverCopyReveal) * 10}px`);
        storySticky.style.setProperty("--story-copy-river-y", `${(1 - riverCopyReveal) * 32}px`);

        if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
          const panelIndex = progress >= 0.61 ? 2 : progress >= 0.26 ? 1 : 0;
          if (!storyPanelsTrackedRef.current.has(panelIndex)) {
            storyPanelsTrackedRef.current.add(panelIndex);
            analyticsService.trackStoryPanelView(panelIndex, STORY_PANELS[panelIndex]);
          }
        }
      } else if (storySticky) {
        const panels = Array.from(storySticky.querySelectorAll<HTMLElement>(".story-copy"));
        panels.forEach((panel, panelIndex) => {
          const panelRect = panel.getBoundingClientRect();
          const visible = panelRect.top < window.innerHeight * 0.8
            && panelRect.bottom > window.innerHeight * 0.2;
          if (visible && !storyPanelsTrackedRef.current.has(panelIndex)) {
            storyPanelsTrackedRef.current.add(panelIndex);
            analyticsService.trackStoryPanelView(panelIndex, STORY_PANELS[panelIndex]);
          }
        });
      }

      lastY = y;
      frame = window.requestAnimationFrame(updatePageMotion);
    };

    frame = window.requestAnimationFrame(updatePageMotion);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const sections = [lifeRef.current, servicesRef.current].filter(Boolean) as HTMLElement[];
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion || typeof IntersectionObserver === "undefined") {
      sections.forEach((section) => section.classList.add("is-visible"));
      return;
    }

    rootRef.current?.classList.add("motion-ready");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.18 });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const element = productStageRef.current;
    if (!element) return;

    if (typeof IntersectionObserver === "undefined") {
      setProductVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      setProductVisible(entry.isIntersecting);
      if (entry.isIntersecting) setProductView(0);
    }, { threshold: 0.35 });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (
      !productVisible
      || window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) return;

    const timer = setInterval(() => {
      if (!document.hidden) setProductView((current) => (current + 1) % PRODUCT_VIEWS.length);
    }, 3200);
    return () => clearInterval(timer);
  }, [productVisible]);

  useEffect(() => {
    if (!productVisible || productViewsTrackedRef.current.has(productView)) return;
    productViewsTrackedRef.current.add(productView);
    analyticsService.trackProductView(productView, PRODUCT_VIEWS[productView]);
  }, [productView, productVisible]);

  useEffect(() => {
    const element = capabilitiesRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setCapabilitiesVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setCapabilitiesVisible(entry.isIntersecting), { threshold: 0.35 });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!capabilitiesVisible || capabilitiesPaused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setInterval(() => {
      if (!document.hidden) setCapabilityIndex((current) => (current + 1) % CAPABILITIES.length);
    }, 15000);
    return () => clearInterval(timer);
  }, [capabilitiesPaused, capabilitiesVisible]);

  useEffect(() => {
    const element = proofRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setProofVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setProofVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!proofVisible || proofPaused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setProviderChanging(false);
      return;
    }
    let changeTimer: ReturnType<typeof setTimeout> | undefined;
    const cycleTimer = setInterval(() => {
      if (document.hidden) return;
      setProviderChanging(true);
      changeTimer = setTimeout(() => {
        setProviderIndex((current) => (current + 1) % PROVIDERS.length);
        setProviderChanging(false);
      }, 320);
    }, 3600);
    return () => {
      clearInterval(cycleTimer);
      if (changeTimer) clearTimeout(changeTimer);
    };
  }, [proofPaused, proofVisible]);

  useEffect(() => {
    if (!proofVisible || providerViewsTrackedRef.current.has(providerIndex)) return;
    providerViewsTrackedRef.current.add(providerIndex);
    analyticsService.trackProviderView(providerIndex, PROVIDERS[providerIndex].name);
  }, [proofVisible, providerIndex]);

  return (
    <div className={`homepage-v7${heroIntroComplete && storyImageReady ? " intro-sequence-complete" : ""}`} ref={rootRef}>
      <a className="skip-link" href="#main">Skip to content</a>

      <header className="site-nav" data-theme="light" ref={navRef}>
        <span className="nav-glass" aria-hidden="true" />
        <div className="nav-inner">
          <TrackedSectionLink className="brand" href="#top" aria-label="Plutus home" analyticsLabel="Plutus home" analyticsSection="Landing Nav">
            <img className="brand-mark" ref={logoRef} src="/favicon.png" alt="" />
            <span className="brand-name">Plutus</span>
          </TrackedSectionLink>
          <nav className="nav-links" aria-label="Primary navigation">
            <TrackedSectionLink href="#approach" analyticsLabel="Approach" analyticsSection="Landing Nav">Approach</TrackedSectionLink>
            <TrackedSectionLink href="#product" analyticsLabel="Product" analyticsSection="Landing Nav">Product</TrackedSectionLink>
            <TrackedSectionLink href="#services" analyticsLabel="What we do" analyticsSection="Landing Nav">What we do</TrackedSectionLink>
            <TrackedSectionLink href="#proof" analyticsLabel="Research" analyticsSection="Landing Nav">Research</TrackedSectionLink>
            <TrackedSectionLink href="#fees" analyticsLabel="Pricing" analyticsSection="Landing Nav">Pricing</TrackedSectionLink>
          </nav>
          <div className="nav-actions">
            <MarketingLink campaign="landing_nav_login" label="Log in" section="Landing Nav" className="nav-login">Log in</MarketingLink>
            <MarketingLink campaign="landing_nav_build_strategy" label="Build your strategy" section="Landing Nav" className="nav-cta" toGate>Build your strategy</MarketingLink>
            <button
              className="menu-toggle"
              type="button"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              onClick={toggleMobileMenu}
            ><span /></button>
          </div>
        </div>
      </header>

      <aside
        className={`mobile-menu${mobileMenuOpen ? " is-open" : ""}`}
        id="mobile-menu"
        aria-hidden={!mobileMenuOpen}
        inert={!mobileMenuOpen ? true : undefined}
      >
        <nav aria-label="Mobile navigation">
          <TrackedSectionLink href="#approach" analyticsLabel="Approach" analyticsSection="Landing Mobile Menu" onClick={closeMobileMenu}>Approach</TrackedSectionLink>
          <TrackedSectionLink href="#product" analyticsLabel="Product" analyticsSection="Landing Mobile Menu" onClick={closeMobileMenu}>Product</TrackedSectionLink>
          <TrackedSectionLink href="#services" analyticsLabel="What we do" analyticsSection="Landing Mobile Menu" onClick={closeMobileMenu}>What we do</TrackedSectionLink>
          <TrackedSectionLink href="#proof" analyticsLabel="Research" analyticsSection="Landing Mobile Menu" onClick={closeMobileMenu}>Research</TrackedSectionLink>
          <TrackedSectionLink href="#fees" analyticsLabel="Pricing" analyticsSection="Landing Mobile Menu" onClick={closeMobileMenu}>Pricing</TrackedSectionLink>
          <MarketingLink campaign="landing_mobile_begin" label="Begin" section="Landing Mobile Menu" toGate onClick={closeMobileMenu}><span className="serif">Begin</span></MarketingLink>
        </nav>
        <div className="mobile-meta">
          <span>SEC-registered adviser</span>
          <MarketingLink campaign="landing_mobile_login" label="Log in" section="Landing Mobile Menu">Log in</MarketingLink>
        </div>
      </aside>

      <main id="main">
        <section className="hero" id="top" data-nav-theme="dark" data-analytics-section="hero" data-analytics-label="Hero">
          <div className="page-grid">
            <div className="hero-copy-block">
              <div className="hero-main reveal reveal-1">
                <h1>A portfolio built for what <span className="serif">comes next.</span></h1>
              </div>
              <div className="hero-bottom reveal reveal-2" onAnimationEnd={(event) => { if (event.target === event.currentTarget) setHeroIntroComplete(true); }}>
                <p className="hero-copy">Your holistic strategy, constructed using actively managed portfolios created by independent research managers, executed in your own brokerage account. No hedge fund required.</p>
                <MarketingLink campaign="landing_hero_build_strategy" label="Build your strategy" section="Landing Hero" className="text-link hero-cta">Build your strategy</MarketingLink>
                <HomeGate campaign="landing_hero_gate" section="Landing Hero" />
              </div>
            </div>
            <div className="hero-visual reveal reveal-3" ref={productStageRef}>
              <V7ProductPhone activeView={productView} />
              <div className="product-disclosures">
                <p className="product-disclosure">Numbers for illustrative purposes only, not actual performance. This is just to show you what your product features may look like and not indicative of actual performance which will vary based on many factors. Please read our <TrackedSectionLink href="#disclosures" analyticsLabel="Disclosures" analyticsSection="Landing Product">disclosures</TrackedSectionLink> for more information.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="story" id="story" data-nav-theme="dark" data-analytics-section="story" data-analytics-label="Adviser story" aria-labelledby="story-title" ref={storyRef}>
          <div className="story-sticky" ref={storyStickyRef}>
            <img className="story-frame story-frame--coast" src="/homepage-valence-v2/story-coast-v2.jpg" alt="A person looking across a misty coastal headland" fetchPriority="high" onLoad={() => setStoryImageReady(true)} onError={() => setStoryImageReady(true)} />
            <img className="story-frame story-frame--mountain" src="/homepage-valence-v2/story-mountain-v3.jpg" alt="A person on a mountain ridge under gathering clouds" fetchPriority="high" />
            <img className="story-frame story-frame--river" src="/homepage-valence-v2/story-river-v3.jpg" alt="A small boat moving through a wide river landscape" loading="eager" />
            <div className="story-shade" aria-hidden="true" />
            <div className="story-copy story-copy--coast">
              <h2 id="story-title">We are your investment adviser working on one goal: to provide you a portfolio strategy to <span className="serif">meet your needs.</span><span className="story-adviser-note">Advisory services are provided by Namsoft Advisors LLC only to clients under a written advisory agreement.</span></h2>
              <p className="story-support"><span className="serif">We don't stop there.</span> We manage all required trading so you can get back to life.</p>
            </div>
            <div className="story-copy story-copy--mountain">
              <h2>A portfolio is more than a collection of ideas. It is a system, sized, coordinated, and kept in motion as markets change. The complexity is ours, <span className="serif">not yours.</span></h2>
            </div>
            <div className="story-copy story-copy--river">
              <p className="eyebrow">Who we serve</p>
              <h2>Personal capital. Family capital. Professional capital. <span className="serif">One system.</span></h2>
              <div className="story-audience-list" aria-label="Client types served by Plutus">
                {['Individuals', 'Trusts', 'Retirement accounts', 'Hedge funds'].map((audience, index) => (
                  <p className="story-audience-item" key={audience}>{audience}<span>0{index + 1}</span></p>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="capabilities" id="approach" data-nav-theme="light" data-analytics-section="approach" data-analytics-label="How Plutus works" aria-labelledby="approach-title">
          <div className="page-grid">
            <div className="capabilities-intro">
              <p className="eyebrow">How Plutus works</p>
              <h2 className="section-title" id="approach-title">A portfolio is useful when it works as <span className="serif">one.</span></h2>
              <p className="section-copy">You set the objective and the risk you want. Plutus turns that into one allocation, checks the pieces together, and keeps the strategy on model as markets change.</p>
              <p className="section-copy capabilities-note">If you want to actively make allocation decisions, there is that too.</p>
            </div>

            <div
              className="capability-list"
              ref={capabilitiesRef}
              onMouseEnter={() => setCapabilitiesPaused(true)}
              onMouseLeave={() => setCapabilitiesPaused(false)}
              onFocus={() => setCapabilitiesPaused(true)}
              onBlur={() => setCapabilitiesPaused(false)}
            >
              {CAPABILITIES.map((capability, index) => {
                const open = capabilityIndex === index;
                return (
                  <article className={`capability${open ? " is-open" : ""}`} key={capability.title}>
                    <button
                      className="capability-button"
                      type="button"
                      aria-expanded={open}
                      onClick={() => {
                        setCapabilityIndex(index);
                        analyticsService.trackCapabilitySelected(index, capability.title);
                      }}
                    >
                      <span className="capability-title">{capability.title}</span>
                      <span className="capability-icon" aria-hidden="true" />
                    </button>
                    <div className="capability-panel"><div><p>{capability.copy}</p></div></div>
                    <div className="capability-progress" aria-hidden="true" />
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="product" id="product" data-nav-theme="light" data-analytics-section="product" data-analytics-label="Introductory offer" aria-labelledby="product-title">
          <div className="page-grid">
            <aside className="product-offer" aria-label="Introductory advisory fee waiver">
              <p className="product-offer-label">Introductory offer</p>
              <div className="product-offer-body">
                <h3 id="product-title">We think you'll <span className="serif">love Plutus.</span></h3>
                <p className="product-offer-title">So your first <span className="serif">30 days</span> come with no Plutus advisory fee.</p>
                <p className="product-offer-copy">Available to eligible new clients. Standard advisory fees begin after the introductory period unless the advisory relationship is terminated. Brokerage, fund, tax, and other third-party costs may apply.</p>
                <div className="product-offer-actions">
                  <MarketingLink campaign="landing_product_offer_build_strategy" label="Build your strategy" section="Landing Product Offer" className="text-link" toGate>Build your strategy</MarketingLink>
                  <TrackedSectionLink className="product-offer-terms" href="#introductory-fee-waiver" analyticsLabel="Offer terms" analyticsSection="Landing Product Offer">Offer terms</TrackedSectionLink>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="proof" id="proof" data-nav-theme="dark" data-analytics-section="proof" data-analytics-label="Research ecosystem" aria-labelledby="proof-title">
          <div className="page-grid">
            <div className="proof-copy">
              <p className="eyebrow">Research ecosystem</p>
              <h2 className="section-title" id="proof-title">Independent research, brought into one <span className="serif">portfolio.</span></h2>
              <p className="section-copy">Plutus gives independent research a shared portfolio context so each idea can be judged by what it adds to the whole.</p>
            </div>
            <div
              className="proof-reel"
              ref={proofRef}
              onMouseEnter={() => setProofPaused(true)}
              onMouseLeave={() => setProofPaused(false)}
              onFocus={() => setProofPaused(true)}
              onBlur={() => setProofPaused(false)}
            >
              <p className="proof-reel-label eyebrow">Some of our model portfolio providers</p>
              <p className={`proof-provider${providerChanging ? " is-changing" : ""}`}>{PROVIDERS[providerIndex].name}</p>
              <div className="proof-list">
                {PROVIDERS.map((provider, index) => {
                  const path = provider.providerId === null ? null : getPublicProviderProfilePath(provider.providerId);
                  const className = `proof-name${providerIndex === index ? " is-active" : ""}`;
                  const label = <><span>{provider.name}</span><span aria-hidden="true">0{index + 1}</span></>;
                  return path
                    ? <Link to={path} className={className} key={provider.name} onClick={() => analyticsService.trackNavigationClick(provider.name, "Research ecosystem", path)}>{label}</Link>
                    : <div className={className} key={provider.name}>{label}</div>;
                })}
              </div>
              <Link to="/providers" className="proof-directory-link" onClick={() => analyticsService.trackNavigationClick("Browse all providers", "Research ecosystem", "/providers")}>Browse all providers <span aria-hidden="true">↗</span></Link>
            </div>
          </div>
        </section>

        <section className="services" id="services" data-nav-theme="light" data-analytics-section="services" data-analytics-label="Operating system" aria-labelledby="services-title" ref={servicesRef}>
          <div className="page-grid">
            <div className="services-head">
              <p className="eyebrow">One operating system</p>
              <h2 className="section-title" id="services-title">Your portfolio deserves a system that sees the whole of it, and keeps every part aligned as markets <span className="serif">move.</span></h2>
              <p>Not another watchlist. One strategy that connects research, construction, trading, and oversight in the account you control.</p>
            </div>
          </div>
          <div className="service-slider" aria-label="Plutus operating system">
            <article className="service-card">
              <span className="service-number">01 / Research</span>
              <span className="service-symbol" aria-hidden="true"><svg viewBox="0 0 72 72"><circle cx="36" cy="36" r="25" /><path d="M20 42c9-2 12-15 20-18 5-2 9 1 13 6M26 49c6-7 14-9 25-8" /></svg></span>
              <h3>Independent model access</h3>
              <p>Bring professionally published investment models into one investable strategy.</p>
            </article>
            <article className="service-card">
              <span className="service-number">02 / Construction</span>
              <span className="service-symbol" aria-hidden="true"><svg viewBox="0 0 72 72"><circle cx="36" cy="36" r="25" /><circle cx="36" cy="36" r="12" /><path d="M36 11v13M36 48v13M11 36h13M48 36h13" /></svg></span>
              <h3>Whole-portfolio allocation</h3>
              <p>Weight the pieces by objective, time horizon, and risk, with concentration and overlap visible.</p>
            </article>
            <article className="service-card">
              <span className="service-number">03 / Execution</span>
              <span className="service-symbol" aria-hidden="true"><svg viewBox="0 0 72 72"><path d="M12 50c10-2 14-19 23-20 8-1 9 11 17 10 4-1 7-5 9-10" /><path d="M53 23l9 6-8 7" /><circle cx="18" cy="49" r="4" /></svg></span>
              <h3>Continuous execution and oversight</h3>
              <p>Translate model changes into trades, rebalance when targets move, and keep the whole portfolio visible in the account you control.</p>
            </article>
          </div>
        </section>

        <section className="safeguards" id="safeguards" data-nav-theme="light" data-analytics-section="safeguards" data-analytics-label="Custody and control" aria-labelledby="safeguards-title">
          <div className="page-grid">
            <div className="safeguards-head">
              <p className="eyebrow">Custody and control</p>
              <h2 className="section-title" id="safeguards-title">Your assets remain yours. Our authority stays <span className="serif">limited.</span></h2>
              <p className="section-copy">Your assets remain at your brokerage. Plutus cannot withdraw or transfer assets to third parties. The only account debit we can initiate is the advisory fee you authorize.</p>
            </div>
            <div className="safeguards-grid">
              {SAFEGUARDS.map((item, index) => (
                <article className="safeguard-item" key={item.title}>
                  <span className="safeguard-number">0{index + 1}</span>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="life-horizon" id="life" data-nav-theme="dark" data-analytics-section="life" data-analytics-label="Portfolio direction" aria-labelledby="life-title" ref={lifeRef}>
          <div className="life-horizon-media" aria-hidden="true"><img src="/homepage-v5/horizon-sailboat-v5.jpg" alt="" loading="lazy" /></div>
          <div className="life-horizon-shade" aria-hidden="true" />
          <div className="page-grid life-horizon-layout">
            <div className="life-horizon-copy">
              <p className="eyebrow">A portfolio with direction</p>
              <h2 id="life-title">Your portfolio, pointed toward the <span className="serif">horizon of your needs.</span></h2>
              <p>Every goal sits at a different distance. Plutus keeps the portfolio moving in the direction you set, while the strategy adapts to the markets along the way.</p>
            </div>
            <aside className="life-horizon-aside" aria-label="Thousands of unique strategies. Because everyone has unique needs.">
              <p className="life-strategy-count" aria-hidden="true"><span>Thousands</span> of unique strategies.</p>
              <p className="life-strategy-sub" aria-hidden="true">Because everyone has unique needs.</p>
            </aside>
          </div>
        </section>

        <section className="fees" id="fees" data-nav-theme="light" data-analytics-section="fees" data-analytics-label="Pricing" aria-labelledby="fees-title">
          <div className="fees-inner">
            <p className="eyebrow">Pricing</p>
            <p className="fee-rate" aria-label="One percent per year">1%</p>
            <h2 id="fees-title">per year on the assets we advise</h2>
            <p className="fee-summary">pay as you go, cancel anytime</p>
            <p className="fee-promise">One simple fee. No performance fees, no hidden costs.</p>
            <p className="fee-note">Plutus charges no separate trade commission or performance allocation. Brokerage commissions, option premiums, fund expenses, custody charges, taxes, and other third-party costs may apply. Review the <ExternalDocumentLink href={RELATIONSHIP_SUMMARY_CRS} documentName="Form CRS" section="Landing Pricing">Form CRS</ExternalDocumentLink> and <ExternalDocumentLink href={DISCLOSURE_BROCHURE} documentName="Form ADV Part 2A disclosure brochure" section="Landing Pricing">disclosure brochure</ExternalDocumentLink>.</p>
          </div>
        </section>

        <section className="backing" id="backing" data-nav-theme="light" data-analytics-section="backing" data-analytics-label="Venture backing" aria-labelledby="backing-title">
          <div className="page-grid">
            <div className="backing-head">
              <p className="eyebrow">Built for the long term</p>
              <h2 className="section-title" id="backing-title">Venture-backed. Built to <span className="serif">endure.</span></h2>
              <p className="section-copy">Plutus is backed by investors who understand that trusted financial infrastructure is built carefully, over time. Their support helps us invest in the research, technology, and operations behind every client experience.</p>
            </div>
            <div className="backing-logos" aria-label="Plutus investors">
              <article className="investor-card investor-card--visse"><figure><img src="/logo-visse.png" alt="Visse Capital" /></figure><div><p>Visse Capital</p><span>Investor</span></div></article>
              <article className="investor-card"><figure><img src="/logo-tarantula.png" alt="Tarantula Capital" /></figure><div><p>Tarantula Capital</p><span>Investor</span></div></article>
              <article className="investor-card"><figure><img src="/logo-rocketship.png" alt="Rocketship.VC" /></figure><div><p>Rocketship.VC</p><span>Investor</span></div></article>
            </div>
            <p className="backing-disclosure">Certain firms and individuals are identified on this website as investors in Namsoft Tech LLC or parent. Such references are included for informational purposes only and do not constitute an endorsement, testimonial, or recommendation of our investment advisory services by any such firm or individual.</p>
          </div>
        </section>

        <section className="disclosures" id="disclosures" data-nav-theme="light" data-analytics-section="disclosures" data-analytics-label="Important disclosures" aria-labelledby="disclosures-title">
          <div className="page-grid">
            <div className="disclosures-head">
              <p className="eyebrow">Important disclosures</p>
              <h2 className="section-title" id="disclosures-title">Read before you <span className="serif">invest.</span></h2>
              <p className="section-copy">The information below describes important limits, risks, and terms that apply to this website and to Namsoft Advisors LLC's advisory services.</p>
              <div className="regulatory-docs" aria-label="Regulatory documents">
                <p>Regulatory documents</p>
                <ExternalDocumentLink href={FORM_ADV_PDF} documentName="Form ADV" section="Landing Disclosures">Form ADV</ExternalDocumentLink>
                <ExternalDocumentLink href={DISCLOSURE_BROCHURE} documentName="Form ADV Part 2A disclosure brochure" section="Landing Disclosures">Form ADV Part 2A disclosure brochure</ExternalDocumentLink>
                <ExternalDocumentLink href={RELATIONSHIP_SUMMARY_CRS} documentName="Client Relationship Summary (Form CRS)" section="Landing Disclosures">Client Relationship Summary (Form CRS)</ExternalDocumentLink>
                <TrackedSectionLink href="mailto:hello@runplutus.com" analyticsLabel="Email Plutus" analyticsSection="Landing Disclosures">hello@runplutus.com</TrackedSectionLink>
              </div>
            </div>
            <div className="disclosure-list">
              <article className="disclosure-item" id="introductory-fee-waiver"><h3>Introductory Advisory Fee Waiver</h3><p>Eligible new clients receive a waiver of the advisory fee charged by Namsoft Advisors LLC during the first 30 days of the advisory relationship. The 30-day period begins on the effective date stated in the written offer or advisory agreement. When the waiver expires, the standard advisory fee disclosed in the advisory agreement will begin unless the client terminates the advisory relationship. The waiver does not apply to fees or costs charged by brokerages, custodians, funds, issuers, taxing authorities, or other third parties. Eligibility, exclusions, and final terms are governed by the written offer and advisory agreement.</p></article>
              <article className="disclosure-item"><h3>General Information Only</h3><p>The information contained on this website is provided for informational purposes only and should not be construed as the provision of personalized investment advice, or an offer to sell or the solicitation of any offer to buy any securities. Nothing on this website constitutes investment, legal, tax, or other professional advice, and you should not rely on the content of this website when making investment or financial decisions. You should consult your professional advisors before determining whether to make any investment decisions through the platform. Investing involves risk, including the potential to lose all amounts invested.</p></article>
              <article className="disclosure-item"><h3>Advisory Services and Relationship</h3><p>Investment advisory services are offered only by Namsoft Advisors LLC (&quot;Namsoft Advisors&quot;) and only to clients who have entered into a written advisory agreement with Namsoft Advisors. No advisory relationship is created by your use of this website or through any communication with Namsoft Advisors absent a signed advisory agreement. For more information about Namsoft Advisors, including information about our services, fees, and potential conflicts of interest, please review our <ExternalDocumentLink href={FORM_ADV_PDF} documentName="Form ADV" section="Landing Disclosures">Form ADV</ExternalDocumentLink>, our <ExternalDocumentLink href={DISCLOSURE_BROCHURE} documentName="Form ADV Part 2A disclosure brochure" section="Landing Disclosures">Form ADV Part 2A disclosure brochure</ExternalDocumentLink>, and our <ExternalDocumentLink href={RELATIONSHIP_SUMMARY_CRS} documentName="Client Relationship Summary (Form CRS)" section="Landing Disclosures">Client Relationship Summary (Form CRS)</ExternalDocumentLink>, or contact us at <TrackedSectionLink href="mailto:hello@runplutus.com" analyticsLabel="Email Plutus" analyticsSection="Landing Disclosures">hello@runplutus.com</TrackedSectionLink>. We encourage you to review these documents carefully before entering into any advisory relationship with Namsoft Advisors.</p></article>
              <article className="disclosure-item"><h3>Third Party Information</h3><p>This website contains information about various services and products made available by third parties. Although we believe these third-party sources to be reliable, we make no representations as to the accuracy or completeness of any information derived from such third-party sources and take no responsibility, therefore. Third-party data and information, including market data, fund performance, and research, may be outdated, incomplete, or inaccurate. Neither Namsoft Advisors nor any of its affiliates (collectively, &quot;Namsoft&quot;) has independently verified such information or guarantees its accuracy.</p></article>
              <article className="disclosure-item"><h3>No Warranty; Limitation of Liability</h3><p>The website and all content are provided &quot;as is&quot; without warranties of any kind, either express or implied. Namsoft expressly disclaims all warranties, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the website will be uninterrupted, error-free, or free from viruses or other harmful components.</p><p>To the fullest extent permitted by law, none of Namsoft nor any of their respective owners, officers, directors, employees, contractors, representatives, or agents, or any of their successors or assigns will be liable for any losses, liabilities, damages, or expenses arising out of your use of the website or any investment decisions made based on information contained herein.</p></article>
              <article className="disclosure-item"><h3>Forward-Looking Statements</h3><p>This website may contain forward-looking statements regarding market conditions, investment strategies, or expected performance. Such statements are subject to risks and uncertainties and actual results may differ materially. Past performance is not indicative of future results.</p></article>
              <article className="disclosure-item"><h3>Changes to Website Content</h3><p>Information on this website may be changed, updated, or removed at any time without notice. We are under no obligation to update information contained on this website, even if such information becomes inaccurate or outdated.</p></article>
              <article className="disclosure-item"><h3>Restricted Jurisdictions</h3><p>This website is intended for use by U.S. residents only. The information on this website should not be considered an offer or solicitation of services in any jurisdiction outside of the United States.</p></article>
              <article className="disclosure-item"><h3>Investor References</h3><p>Certain firms and individuals are identified on this website as investors in Namsoft Tech LLC or parent. Such references are included for informational purposes only and do not constitute an endorsement, testimonial, or recommendation of our investment advisory services by any such firm or individual.</p></article>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer" id="start" data-nav-theme="dark" data-analytics-section="footer" data-analytics-label="Footer">
        <div className="page-grid">
          <nav className="footer-links" aria-label="Footer navigation">
            <TrackedSectionLink href="#approach" analyticsLabel="How it works" analyticsSection="Landing Footer">How it works</TrackedSectionLink>
            <TrackedSectionLink href="#product" analyticsLabel="Product" analyticsSection="Landing Footer">Product</TrackedSectionLink>
            <TrackedSectionLink href="#services" analyticsLabel="What we do" analyticsSection="Landing Footer">What we do</TrackedSectionLink>
            <TrackedSectionLink href="#proof" analyticsLabel="Research" analyticsSection="Landing Footer">Research</TrackedSectionLink>
            <TrackedSectionLink href="#disclosures" analyticsLabel="Disclosures" analyticsSection="Landing Footer">Disclosures</TrackedSectionLink>
            <a
              href="https://x.com/RunPlutus"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analyticsService.trackNavigationClick("X / Twitter", "Landing Footer", "https://x.com/RunPlutus")}
            >
              X / Twitter
            </a>
            <Link to="/terms" onClick={() => analyticsService.trackNavigationClick("Terms of Service", "Landing Footer", "/terms")}>Terms of Service</Link>
            <Link to="/privacy" onClick={() => analyticsService.trackNavigationClick("Privacy Policy", "Landing Footer", "/privacy")}>Privacy Policy</Link>
            <Link to="/llms" onClick={() => analyticsService.trackNavigationClick("For LLMs", "Landing Footer", "/llms")}>For LLMs</Link>
            <ExternalDocumentLink href={FORM_ADV_PDF} documentName="Form ADV" section="Landing Footer">Form ADV</ExternalDocumentLink>
            <ExternalDocumentLink href={DISCLOSURE_BROCHURE} documentName="Form ADV Part 2A disclosure brochure" section="Landing Footer">ADV Part 2A Brochure</ExternalDocumentLink>
            <ExternalDocumentLink href={RELATIONSHIP_SUMMARY_CRS} documentName="Client Relationship Summary (Form CRS)" section="Landing Footer">Form CRS</ExternalDocumentLink>
            <MarketingLink campaign="landing_footer_login" label="Log in" section="Landing Footer">Log in</MarketingLink>
          </nav>
          <div className="footer-lead">
            <h2>Your money, organized around the life you are <span className="serif">building.</span></h2>
            <MarketingLink campaign="landing_footer_build_strategy" label="Build your strategy" section="Landing Footer" className="text-link" toGate>Build your strategy</MarketingLink>
          </div>
          <div className="footer-brand"><img src="/favicon.png" alt="" /><span>Plutus</span></div>
          <div className="footer-legal"><p>The information on this website is for informational purposes only and is not personalized investment advice. Investing involves risk, including possible loss of principal. Assets remain in your brokerage account. Plutus cannot withdraw or transfer assets to third parties; the only account debit we can initiate is the advisory fee you authorize.</p><p>Investment advisory services are offered by Namsoft Advisors LLC, an SEC-registered investment adviser, to clients under a written advisory agreement. SEC registration does not imply a certain level of skill or training. Past performance is not indicative of future results.</p></div>
          <div className="footer-bottom"><p>Namsoft Advisors LLC is an SEC-registered investment advisor. Registration with the SEC does not imply a certain level of skill or training. Past performance is not indicative of future results.</p><p>Namsoft Tech LLC. All rights reserved.</p></div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
